import React, { useEffect, useRef, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box, Typography, IconButton, Tooltip, Chip, CircularProgress, Button,
  TextField, Menu, MenuItem,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import HistoryIcon from '@mui/icons-material/History';
import SendIcon from '@mui/icons-material/Send';
import DownloadIcon from '@mui/icons-material/Download';
import { useAuth } from '../../../../contexts/AuthContext';
import { listenMockupApp, listVersions } from '../services/mockupService';
import { APPLAB_ACCENT, APPLAB_ORANGE } from '../../constants';

const SUGGESTIONS = [
  'Gör dashboarden mer kompakt',
  'Byt till mörkt tema',
  'Lägg till en risklogg-sektion',
  'Gör rubrikerna mer framträdande',
  'Lägg till en Gantt-liknande tidplan',
  'Byt färgerna till mer dämpade toner',
  'Lägg till ett KPI-kort till',
  'Gör det mer minimalistiskt',
];

function ChatMessage({ msg }) {
  const isUser = msg.role === 'user';
  return (
    <Box sx={{
      display: 'flex', flexDirection: 'column',
      alignItems: isUser ? 'flex-end' : 'flex-start',
      gap: 0.5, mb: 1.5,
    }}>
      <Box sx={{
        maxWidth: '85%', px: 2, py: 1.25, borderRadius: isUser ? '12px 12px 4px 12px' : '12px 12px 12px 4px',
        bgcolor: isUser ? APPLAB_ACCENT + '22' : 'rgba(255,255,255,0.05)',
        border: `1px solid ${isUser ? APPLAB_ACCENT + '44' : 'rgba(255,255,255,0.08)'}`,
      }}>
        <Typography sx={{ color: isUser ? '#c8ede8' : '#ccc', fontSize: '0.82rem', lineHeight: 1.6, whiteSpace: 'pre-wrap' }}>
          {msg.content}
        </Typography>
      </Box>
      {msg.tokens && (
        <Typography sx={{ color: '#333', fontSize: '0.68rem' }}>
          {msg.tokens.toLocaleString('sv')} tokens
        </Typography>
      )}
    </Box>
  );
}

export default function MockupEditor() {
  const { appId }        = useParams();
  const navigate         = useNavigate();
  const { currentUser }  = useAuth();

  const [app, setApp]           = useState(null);
  const [versions, setVersions] = useState([]);
  const [loading, setLoading]   = useState(true);
  const [historyAnchor, setHistoryAnchor] = useState(null);
  const [iframeHtml, setIframeHtml] = useState(null);
  const [htmlLoading, setHtmlLoading] = useState(false);

  // Chat state
  const [messages, setMessages]   = useState([]);
  const [input, setInput]         = useState('');
  const [iterating, setIterating] = useState(false);
  const chatEndRef                = useRef(null);

  useEffect(() => {
    if (!appId) return;
    const unsub = listenMockupApp(appId, (data) => {
      setApp(prev => {
        // When status flips from iterating → done, mark iteration as resolved
        if (prev?.status === 'iterating' && data.status === 'done') {
          setIterating(false);
          setMessages(m => [
            ...m,
            {
              role:    'assistant',
              content: 'Klart! Mockupen är uppdaterad.',
              tokens:  null,
            },
          ]);
        }
        if (prev?.status === 'iterating' && data.status === 'error') {
          setIterating(false);
          setMessages(m => [
            ...m,
            { role: 'assistant', content: `Fel: ${data.error_msg || 'Något gick fel.'}`, tokens: null },
          ]);
        }
        return data;
      });
      setLoading(false);
    });
    listVersions(appId).then(setVersions).catch(() => {});
    return unsub;
  }, [appId]);

  useEffect(() => {
    chatEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  async function sendIteration(text) {
    const instr = (text || input).trim();
    if (!instr || iterating || !currentUser) return;
    setInput('');
    setIterating(true);
    setMessages(m => [...m, { role: 'user', content: instr }]);

    try {
      const token = await currentUser.getIdToken();
      const res = await fetch('/.netlify/functions/applab-mockup-iterate-background', {
        method:  'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body:    JSON.stringify({ appId, instruction: instr }),
      });
      if (!res.ok && res.status !== 202) {
        throw new Error(await res.text().catch(() => 'Nätverksfel'));
      }
      // Status 202 = background function accepted — Firestore listener handles the rest
      setMessages(m => [...m, { role: 'assistant', content: 'AI:n jobbar på det…', tokens: null }]);
    } catch (e) {
      setIterating(false);
      setMessages(m => [...m, { role: 'assistant', content: `Fel: ${e.message}`, tokens: null }]);
    }
  }

  function downloadHtml() {
    if (!app?.public_url) return;
    fetch(app.public_url)
      .then(r => r.blob())
      .then(blob => {
        const a = document.createElement('a');
        a.href = URL.createObjectURL(blob);
        a.download = `mockup-${app.name || appId}.html`;
        a.click();
      })
      .catch(() => {});
  }

  const publicUrl = app?.public_url;
  const status    = app?.status;

  // Fetch HTML and use srcDoc to bypass cross-origin iframe restrictions
  useEffect(() => {
    if (!publicUrl) return;
    setHtmlLoading(true);
    setIframeHtml(null);
    fetch(publicUrl + '?t=' + Date.now())
      .then(r => r.text())
      .then(html => setIframeHtml(html))
      .catch(() => setHtmlLoading(false))
      .finally(() => setHtmlLoading(false));
  }, [publicUrl]);

  if (loading) {
    return (
      <Box sx={{ minHeight: '100vh', bgcolor: '#0e0e0e', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <CircularProgress sx={{ color: APPLAB_ACCENT }} />
      </Box>
    );
  }

  if (!app) {
    return (
      <Box sx={{ minHeight: '100vh', bgcolor: '#0e0e0e', color: 'white', display: 'flex', alignItems: 'center', justifyContent: 'center', flexDirection: 'column', gap: 2 }}>
        <Typography>Mockupen hittades inte.</Typography>
        <Button onClick={() => navigate('/applab/mockup')} sx={{ color: APPLAB_ACCENT, textTransform: 'none' }}>Tillbaka</Button>
      </Box>
    );
  }

  return (
    <Box sx={{ height: '100vh', bgcolor: '#0e0e0e', color: 'white', display: 'flex', flexDirection: 'column' }}>

      {/* ── Toolbar ──────────────────────────────────────────────────────────── */}
      <Box sx={{
        bgcolor: '#1a1a1a', borderBottom: '1px solid rgba(255,255,255,0.06)',
        px: 2, py: 1, display: 'flex', alignItems: 'center', gap: 1.5, flexShrink: 0,
      }}>
        <Tooltip title="MockupN">
          <IconButton onClick={() => navigate('/applab/mockup')} size="small" sx={{ color: '#888', '&:hover': { color: 'white' } }}>
            <ArrowBackIcon sx={{ fontSize: 18 }} />
          </IconButton>
        </Tooltip>

        <Typography sx={{ color: '#555', fontSize: '0.78rem', flexShrink: 0 }}>
          App<span style={{ color: APPLAB_ORANGE }}>LAB</span> · MockupN
        </Typography>
        <Box sx={{ width: 1, height: 16, bgcolor: 'rgba(255,255,255,0.08)', flexShrink: 0 }} />

        <Typography fontWeight={700} fontSize="0.88rem" sx={{ mr: 'auto', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          {app.name?.replace('MockupN – ', '') || 'Namnlös mockup'}
        </Typography>

        {/* Status */}
        {(status === 'generating' || status === 'iterating') && (
          <Chip
            icon={<CircularProgress size={10} sx={{ color: 'white !important' }} />}
            label={status === 'iterating' ? 'Itererar…' : 'Genererar…'}
            size="small"
            sx={{ bgcolor: 'rgba(255,200,0,0.12)', color: '#ffd740', border: '1px solid rgba(255,215,64,0.25)' }}
          />
        )}
        {status === 'done' && (
          <Chip label="Klar" size="small" sx={{ bgcolor: APPLAB_ACCENT + '22', color: APPLAB_ACCENT, border: `1px solid ${APPLAB_ACCENT}44` }} />
        )}
        {status === 'error' && (
          <Chip label="Fel" size="small" sx={{ bgcolor: 'rgba(239,83,80,0.12)', color: '#ef5350', border: '1px solid rgba(239,83,80,0.25)' }} />
        )}

        {/* Version history */}
        {versions.length > 1 && (
          <>
            <Tooltip title="Versioner">
              <IconButton size="small" onClick={e => setHistoryAnchor(e.currentTarget)} sx={{ color: '#888', '&:hover': { color: 'white' } }}>
                <HistoryIcon sx={{ fontSize: 18 }} />
              </IconButton>
            </Tooltip>
            <Menu
              anchorEl={historyAnchor}
              open={Boolean(historyAnchor)}
              onClose={() => setHistoryAnchor(null)}
              PaperProps={{ sx: { bgcolor: '#222', color: 'white', minWidth: 220 } }}
            >
              {versions.map(v => (
                <MenuItem
                  key={v.id}
                  onClick={() => setHistoryAnchor(null)}
                  sx={{ fontSize: '0.8rem', color: v.version_number === app.current_version ? APPLAB_ACCENT : '#aaa' }}
                >
                  v{v.version_number} — {v.instruction ? v.instruction.slice(0, 40) : 'Original'}
                  {v.version_number === app.current_version && ' ✓'}
                </MenuItem>
              ))}
            </Menu>
          </>
        )}

        {publicUrl && (
          <>
            <Tooltip title="Ladda ner HTML">
              <IconButton size="small" onClick={downloadHtml} sx={{ color: '#888', '&:hover': { color: 'white' } }}>
                <DownloadIcon sx={{ fontSize: 18 }} />
              </IconButton>
            </Tooltip>
            <Tooltip title="Öppna i nytt fönster">
              <IconButton size="small" component="a" href={publicUrl} target="_blank" rel="noopener noreferrer" sx={{ color: '#888', '&:hover': { color: 'white' } }}>
                <OpenInNewIcon sx={{ fontSize: 18 }} />
              </IconButton>
            </Tooltip>
          </>
        )}
      </Box>

      {/* ── Body: split preview + chat ──────────────────────────────────────── */}
      <Box sx={{ flex: 1, overflow: 'hidden', display: 'flex' }}>

        {/* Preview (left 60%) */}
        <Box sx={{ flex: '0 0 60%', borderRight: '1px solid rgba(255,255,255,0.06)', overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
          {(status === 'generating') && (
            <Box sx={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', flexDirection: 'column', gap: 2 }}>
              <CircularProgress sx={{ color: APPLAB_ACCENT }} />
              <Typography sx={{ color: '#666', fontSize: '0.88rem' }}>Genererar mockup…</Typography>
            </Box>
          )}
          {status === 'error' && !iterating && (
            <Box sx={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', flexDirection: 'column', gap: 2, px: 3 }}>
              <Typography fontSize="2rem">⚠️</Typography>
              <Typography sx={{ color: '#888', textAlign: 'center' }}>{app.error_msg || 'Generering misslyckades.'}</Typography>
              <Button onClick={() => navigate('/applab/mockup/new')} variant="contained" sx={{ bgcolor: APPLAB_ACCENT, textTransform: 'none', fontWeight: 700, '&:hover': { bgcolor: '#00796B' } }}>
                Börja om
              </Button>
            </Box>
          )}
          {(status === 'done' || status === 'iterating') && publicUrl && (
            <Box sx={{ flex: 1, position: 'relative' }}>
              {htmlLoading && (
                <Box sx={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', bgcolor: '#0e0e0e', zIndex: 2 }}>
                  <CircularProgress sx={{ color: APPLAB_ACCENT }} />
                </Box>
              )}
              {iframeHtml && (
                <iframe
                  key={publicUrl}
                  srcDoc={iframeHtml}
                  title="MockupN preview"
                  style={{ width: '100%', height: '100%', border: 'none', display: 'block' }}
                  sandbox="allow-scripts allow-popups allow-forms allow-modals"
                />
              )}
              {(iterating || htmlLoading) && (
                <Box sx={{
                  position: 'absolute', inset: 0, bgcolor: 'rgba(0,0,0,0.45)',
                  display: 'flex', alignItems: 'center', justifyContent: 'center', flexDirection: 'column', gap: 1.5,
                  zIndex: 3,
                }}>
                  <CircularProgress sx={{ color: APPLAB_ACCENT }} />
                  <Typography sx={{ color: '#ccc', fontSize: '0.85rem' }}>
                    {iterating ? 'Uppdaterar mockup…' : 'Laddar…'}
                  </Typography>
                </Box>
              )}
            </Box>
          )}
        </Box>

        {/* Chat panel (right 40%) */}
        <Box sx={{ flex: '0 0 40%', display: 'flex', flexDirection: 'column', bgcolor: '#141414' }}>

          {/* Chat header */}
          <Box sx={{ px: 2.5, py: 2, borderBottom: '1px solid rgba(255,255,255,0.06)' }}>
            <Typography fontWeight={700} fontSize="0.88rem" sx={{ color: 'white', mb: 0.25 }}>
              Iterera med AI
            </Typography>
            <Typography sx={{ color: '#555', fontSize: '0.75rem' }}>
              Berätta vad du vill ändra — AI:n uppdaterar mockupen.
            </Typography>
          </Box>

          {/* Suggestions */}
          {messages.length === 0 && (
            <Box sx={{ px: 2.5, pt: 2, pb: 1 }}>
              <Typography sx={{ color: '#444', fontSize: '0.72rem', mb: 1.25 }}>Förslag</Typography>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75 }}>
                {SUGGESTIONS.map(s => (
                  <Box
                    key={s}
                    onClick={() => sendIteration(s)}
                    sx={{
                      px: 1.5, py: 0.75, borderRadius: 1.5, fontSize: '0.75rem',
                      bgcolor: 'rgba(255,255,255,0.04)', color: '#888',
                      border: '1px solid rgba(255,255,255,0.08)',
                      cursor: 'pointer',
                      '&:hover': { bgcolor: APPLAB_ACCENT + '11', color: APPLAB_ACCENT, borderColor: APPLAB_ACCENT + '44' },
                      transition: 'all 0.12s',
                    }}
                  >
                    {s}
                  </Box>
                ))}
              </Box>
            </Box>
          )}

          {/* Message list */}
          <Box sx={{ flex: 1, overflow: 'auto', px: 2.5, py: 2 }}>
            {messages.map((m, i) => <ChatMessage key={i} msg={m} />)}
            <div ref={chatEndRef} />
          </Box>

          {/* Input */}
          <Box sx={{ px: 2.5, py: 2, borderTop: '1px solid rgba(255,255,255,0.06)' }}>
            <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-end' }}>
              <TextField
                fullWidth multiline maxRows={4}
                value={input}
                onChange={e => setInput(e.target.value)}
                onKeyDown={e => {
                  if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); sendIteration(); }
                }}
                disabled={iterating || status === 'generating'}
                placeholder="Vad vill du ändra? (Enter skickar)"
                sx={{
                  '& .MuiOutlinedInput-root': {
                    color: 'white', bgcolor: 'rgba(255,255,255,0.04)', fontSize: '0.85rem',
                    '& fieldset': { borderColor: 'rgba(255,255,255,0.1)' },
                    '&:hover fieldset': { borderColor: 'rgba(255,255,255,0.2)' },
                    '&.Mui-focused fieldset': { borderColor: APPLAB_ACCENT },
                  },
                  '& textarea::placeholder': { color: '#444', opacity: 1 },
                }}
              />
              <IconButton
                onClick={() => sendIteration()}
                disabled={!input.trim() || iterating || status === 'generating'}
                sx={{
                  bgcolor: APPLAB_ACCENT, color: 'white', borderRadius: 2, width: 42, height: 42, flexShrink: 0,
                  '&:hover': { bgcolor: '#00796B' },
                  '&:disabled': { bgcolor: 'rgba(255,255,255,0.06)', color: '#444' },
                }}
              >
                {iterating ? <CircularProgress size={16} sx={{ color: 'inherit' }} /> : <SendIcon sx={{ fontSize: 18 }} />}
              </IconButton>
            </Box>
            <Typography sx={{ color: '#333', fontSize: '0.68rem', mt: 0.75 }}>
              Shift+Enter för ny rad · varje iteration använder ca 5 000–10 000 tokens
            </Typography>
          </Box>
        </Box>
      </Box>
    </Box>
  );
}
